import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import clsx from 'clsx'

import { RoutingPaths } from 'app/routes/routes'

import { parseHotelStars } from 'entities/tour/libs/parseHotelStars.ts'

import { Hotel } from 'shared/api/tours/search'
import { getRawColor } from 'shared/lib/getRawColor.ts'
import { getValidIcon } from 'shared/lib/validateIcon.ts'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types.ts'
import { Badge } from 'shared/ui/badge'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { Paper } from 'shared/ui/paper'
import { StarRating } from 'shared/ui/star-rating'

import { HotelInfoPopup } from './components/hotel-info-popup'
import s from './styles.module.scss'

interface Props {
  data: Hotel
  showServices?: boolean
}

export const HotelInfo = ({ data, showServices = true }: Props) => {
  const { openDialog } = useDialogBottomSheet()
  const { t } = useTranslation()

  const starsNumber = parseHotelStars(data.hotel_stars)

  const onOpenModal = () => {
    openDialog({
      child: <HotelInfoPopup services={data.hotel_services} />,
      backgroundColor: getRawColor('--white'),
    })
  }

  return (
    <Paper
      padding={[16]}
      className={s.paper}
    >
      <div className={s.header}>
        {starsNumber > 0 && <StarRating rating={starsNumber} />}

        {/* <Badge
          color="violet"
          className={s.departureBadge}
        >
          <AppIcon
            id={AllIcons.PLANE}
            width={19}
            height={19}
          />
          {t('tours_block.departure_from')} {data.town_from?.name}
        </Badge> */}
      </div>

      <div className={s.topInfo}>
        <div className={s.resultText}>
          <p className={clsx('bold-16', s.tourName)}>{data.hotel_name}</p>
          <div className={s.tourInfoContainer}>
            <p className={clsx('medium-12', s.tourInfo)}>
              {data.state_name}, {data.region_name}
            </p>

            {data?.latitude && data?.longitude && (
              <Link
                to={RoutingPaths.tours.hotelMap.path()}
                state={{
                  hotelName: data.hotel_name,
                  latitude: data?.latitude,
                  longitude: data?.longitude,
                }}
                className={clsx('medium-12', s.showHotelOnMap)}
              >
                {t('tours_block.show_hotel_on_map')}
              </Link>
            )}
          </div>
        </div>
      </div>

      {data.is_recommended && (
        <Badge>
          <AppIcon
            id={AllIcons.LOGO}
            width={12}
            height={12}
          />
          {t('tours_block.recommended')}
          <AppIcon
            id={AllIcons.LIKE}
            width={12}
            height={12}
          />
        </Badge>
      )}

      {showServices && (
        <>
          <div className={s.divider} />

          <div className={s.infoList}>
            {data.services.map((service, index) => (
              <div
                className={s.infoItem}
                key={index}
              >
                <AppIcon
                  id={getValidIcon(service.icon)}
                  width={16}
                  height={16}
                  className={s.icon}
                />

                <p className={clsx('medium-14', s.infoText)}>{service.name}</p>
              </div>
            ))}
          </div>

          <button
            className={clsx('semi-bold-14', s.moreInfo)}
            onClick={onOpenModal}
          >
            <AppIcon
              id={AllIcons.ARROW_BOTTOM}
              width={24}
              height={24}
            />
            {t('tours_block.all_services')}
          </button>
        </>
      )}
    </Paper>
  )
}
